import React, { useMemo } from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';

/**
 * Per-lag permutation importance with the best lag highlighted
 * (spec §7 J3 — LagProfileChart).
 */
const LagProfileChart = ({ profile = [], bestLag, height = 160 }) => {
  const theme = useTheme();
  const data = useMemo(
    () => [...profile]
      .sort((a, b) => a.lag - b.lag)
      .map((p) => ({
        lag: String(p.lag),
        importance: Math.max(0, p.importance || 0),
        isBest: p.lag === bestLag,
      })),
    [profile, bestLag],
  );

  if (!data.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No lag profile recorded for this edge.
      </Typography>
    );
  }

  return (
    <Box sx={{ height }}>
      <ResponsiveBar
        data={data}
        keys={['importance']}
        indexBy="lag"
        margin={{ top: 8, right: 8, bottom: 32, left: 44 }}
        padding={0.25}
        colors={(bar) => (bar.data.isBest
          ? theme.palette.primary.main
          : alpha(theme.palette.primary.main, 0.35))}
        axisBottom={{
          legend: 'lag (bins before now)',
          legendPosition: 'middle',
          legendOffset: 26,
          tickSize: 0,
        }}
        axisLeft={{ tickSize: 0, tickValues: 4 }}
        enableLabel={false}
        animate={false}
        theme={{
          text: { fill: theme.palette.text.secondary, fontSize: 10 },
          grid: { line: { stroke: alpha(theme.palette.text.primary, 0.06) } },
        }}
        tooltip={({ data: d }) => (
          <Box sx={{ p: 0.75, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 3 }}>
            <Typography variant="caption">
              lag {d.lag}: +{(d.importance * 100).toFixed(2)}% loss when broken
            </Typography>
          </Box>
        )}
      />
    </Box>
  );
};

export default LagProfileChart;
